import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export type RoutingStrategy = 'optimal' | 'leftFirst' | 'rightFirst';

export interface RouteStop {
  locationId?: string | null;
  gridX?: number | null;
  gridY?: number | null;
}

/** Google Maps tarzı alternatif rota adayı (pick-list-optimized / consolidated). */
export interface RouteAlternative {
  strategy: RoutingStrategy;
  label: string;
  estimatedDistance: number;
  isSelected: boolean;
  /** Seçili olmayan en kısa alternatif için harita ghost çizgisi. */
  stopOrder?: RouteStop[] | null;
}

export function parseRouteAlternatives(payload: any): RouteAlternative[] {
  const raw = payload?.alternatives ?? payload?.Alternatives;
  if (!Array.isArray(raw)) return [];
  return raw.map((a: any) => {
    const stopRaw = a.stopOrder ?? a.StopOrder;
    return {
      strategy: (a.strategy ?? a.Strategy ?? 'optimal') as RoutingStrategy,
      label: a.label ?? a.Label ?? '',
      estimatedDistance: Number(a.estimatedDistance ?? a.EstimatedDistance ?? 0),
      isSelected: Boolean(a.isSelected ?? a.IsSelected),
      stopOrder: Array.isArray(stopRaw)
        ? stopRaw.map((s: any) => ({
            locationId: s.locationId ?? s.LocationId ?? null,
            gridX: s.gridX ?? s.GridX ?? null,
            gridY: s.gridY ?? s.GridY ?? null,
          }))
        : null,
    };
  });
}

export interface CreateWaveDto {
  WarehouseId: string;
  OrderIds: string[];
  RoutingStrategy?: string;
}

export interface StartNextWaveDto {
  WarehouseId: string;
  PersonnelId: string;
  PersonnelName?: string;
  WaveType: 'Single' | 'Multi';
  MarketplaceSource?: string;
  RoutingStrategy?: string;
  // true: sadece devam eden dalgayi kontrol et (yeni dalga olusturma/atama yapma).
  // Atama araba okutuldugunda ResumeOnly=false ile yapilir.
  ResumeOnly?: boolean;
  // Coklu toplamada operatorun sectigi "kac siparis toplayacagim?" adedi. null/undefined ise
  // bekleyen tum coklulari (bos ayirma slotu sayisiyla sinirli) alir. Tekli'de dikkate alinmaz.
  BatchSize?: number;
}

// ===== Akıllı Dalga (Smart Wave) =====
export type SmartGroupingMode = 'Auto' | 'Product' | 'VariantFamily' | 'Category' | 'Location';

export interface SmartGroupingRequestDto {
  WarehouseId: string;
  MaxBatchSize?: number;
  MaxBatchItems?: number;
  MinCohesionScore?: number;
  PrioritizeUrgent?: boolean;
  OrderTypeFilter?: 'Single' | 'Multi' | 'All';
  GroupingMode?: SmartGroupingMode;
  LocationProximityThreshold?: number;
  MarketplaceSource?: string;
}

export interface SmartGroupOrder {
  id: string;
  orderNumber: string;
  customerName?: string;
  itemCount: number;
  priority: number;
  orderType: string;
}

export interface SmartGroupProductSummary {
  productId: string;
  productName?: string;
  stockCode?: string;
  orderCount: number;
  totalQuantity: number;
  gridX?: number | null;
  gridY?: number | null;
  categoryId?: string | null;
  categoryName?: string | null;
}

export interface SmartWaveGroup {
  groupIndex: number;
  orders: SmartGroupOrder[];
  topProducts: SmartGroupProductSummary[];
  totalOrders: number;
  totalItems: number;
  cohesionScore: number;
  estimatedPickDistance?: number | null;
  hasUrgentOrders: boolean;
  groupReason?: string;
  dominantCategoryId?: string | null;
  dominantCategoryName?: string | null;
  categoryCount?: number;
}

export interface SmartWaveGroupingResponse {
  groups: SmartWaveGroup[];
  totalPendingOrders: number;
  ungroupedOrders: number;
  algorithmVersion?: string;
}

// ===== Konsolide Toplama (Çoklu / Batch) =====
export interface ConsolidatedOrderBreakdown {
  orderItemId: string;
  orderId: string;
  orderNumber?: string;
  quantity: number;
  pickedQuantity: number;
  status: string;
}

export interface ConsolidatedPickListItem {
  productId: string;
  productName?: string;
  sku?: string;
  barcode?: string;
  totalQuantity: number;
  pickedQuantity: number;
  locationId?: string;
  locationCode?: string;
  locationName?: string;
  sortOrder: number;
  status: string;
  aisleId?: string;
  aisleName?: string;
  aisleSide?: string;
  gridX?: number;
  gridY?: number;
  orderCount: number;
  orders: ConsolidatedOrderBreakdown[];
}

export interface AssignPickerDto {
  WaveId: string;
  PickerId: string;
  PickerName: string;
}

export interface AssignWavePersonnelDto {
  WaveId: string;
  PersonnelId: string;
  PersonnelName: string;
}

export interface AssignCartDto {
  WaveId: string;
  CartId: string;
}

export interface WaveListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  waveType?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  marketplaceSource?: string;
}

export const waveService = {
  getByWarehouse: (warehouseId: string, params?: WaveListParams) => {
    let url = ACCOUNTING_API.WMS_WAVES(warehouseId);
    if (params) {
      const qp = new URLSearchParams();
      if (params.page) qp.append('page', params.page.toString());
      if (params.pageSize) qp.append('pageSize', params.pageSize.toString());
      if (params.status) qp.append('status', params.status);
      if (params.waveType) qp.append('waveType', params.waveType);
      if (params.search) qp.append('search', params.search);
      if (params.sortBy) qp.append('sortBy', params.sortBy);
      if (params.sortDir) qp.append('sortDir', params.sortDir);
      if (params.marketplaceSource) qp.append('marketplaceSource', params.marketplaceSource);
      const qs = qp.toString();
      if (qs) url += `?${qs}`;
    }
    return wmsApi.get(url);
  },

  getDetail: (id: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_DETAIL(id)),

  create: (data: CreateWaveDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_CREATE, data),

  // Is emri secme adimi kaldirildi: kanal + tekli/coklu secince backend uygun dalgayi
  // bulur/olusturur, personele atar ve baslatir. Sonuc: { resumed, wave }.
  startNext: (data: StartNextWaveDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_START_NEXT, data),

  suggestSmartGrouping: (data: SmartGroupingRequestDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_SUGGEST_SMART_GROUPING, data),

  cancel: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_CANCEL(id)),

  // Toplama islemini birakir: dalga personel uzerinden cikarilir ve tekrar
  // atanabilir hale gelir. Siparisler iptal olmaz.
  release: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_RELEASE(id)),

  delete: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_DELETE(id)),

  assignPicker: (data: AssignPickerDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_ASSIGN_PICKER, data),

  assignPersonnelToWave: (data: AssignWavePersonnelDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_ASSIGN_PERSONNEL, data),

  assignCart: (data: AssignCartDto) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_ASSIGN_CART, data),

  unassignPicker: (waveId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_UNASSIGN_PICKER(waveId)),

  unassignPersonnel: (waveId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_UNASSIGN_PERSONNEL(waveId)),

  unassignCart: (waveId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_UNASSIGN_CART(waveId)),

  start: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_START(id)),

  complete: (id: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_COMPLETE(id)),

  getPickList: (id: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_PICK_LIST(id)),

  getPickListOptimized: (id: string, strategy?: RoutingStrategy) => {
    let url = ACCOUNTING_API.WMS_WAVE_PICK_LIST_OPTIMIZED(id);
    if (strategy) url += `?strategy=${strategy}`;
    return wmsApi.get(url);
  },

  getPickListConsolidated: (id: string, strategy?: RoutingStrategy) => {
    let url = ACCOUNTING_API.WMS_WAVE_PICK_LIST_CONSOLIDATED(id);
    if (strategy) url += `?strategy=${strategy}`;
    return wmsApi.get(url);
  },

  pickConsolidated: (waveId: string, data: { ProductId: string; ScannedLocationId?: string; PickedQuantity: number; IsShortPick?: boolean; Reason?: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_PICK_CONSOLIDATED(waveId), data),

  // Toplama sirasinda lokasyona yerinde hizli stok girisi (mini mal kabul).
  adjustStock: (waveId: string, data: { ProductId: string; LocationId: string; Quantity: number; Reason?: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_ADJUST_STOCK(waveId), data),

  // Lokasyonu olmayan urun icin lokasyon onerisi.
  getLocationSuggestion: (waveId: string, productId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_LOCATION_SUGGESTION(waveId, productId)),

  // Lokasyonsuz stogu secilen lokasyona ata.
  assignLocation: (waveId: string, data: { ProductId: string; LocationId: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_ASSIGN_LOCATION(waveId), data),

  rebuildGraph: (warehouseId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAREHOUSE_REBUILD_GRAPH(warehouseId)),

  getSummary: (warehouseId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_SUMMARY(warehouseId)),

  getActive: (warehouseId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_ACTIVE(warehouseId)),

  getByPersonnel: (warehouseId: string, personnelId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_BY_PERSONNEL, { params: { warehouseId, personnelId } }),

  pickItem: (waveId: string, data: { OrderItemId: string; PickedQuantity: number; ScannedLocationId?: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_PICK_ITEM(waveId), data),

  shortPickItem: (waveId: string, data: { OrderItemId: string; PartialQuantity?: number; Reason?: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_SHORT_PICK(waveId), data),

  completePicking: (waveId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_COMPLETE_PICKING(waveId)),

  getPickingProgress: (waveId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_PICKING_PROGRESS(waveId)),

  sortItem: (waveId: string, data: { OrderItemId: string; SlotId: string }) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_SORT_ITEM(waveId), data),

  completeSorting: (waveId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAVE_COMPLETE_SORTING(waveId)),

  getSortingStatus: (waveId: string) =>
    wmsApi.get(ACCOUNTING_API.WMS_WAVE_SORTING_STATUS(waveId)),
};
